import { useEffect, useRef, useState } from 'react';
import { OnboardingPdfReport } from './OnboardingPdfReportContainer';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import {
  selectClient,
  selectClientIsLoading,
  selectSessionAdvisor,
} from '../model/selectors';
import {
  AuthContainer,
  AuthPrimaryButton,
} from '@client/components/AuthComponents';
import { Button } from '@client/components/ui/button';

export function OnboardingPdfReportPrint() {
  const client = useSelector(selectClient);
  const clientIsLoading = useSelector(selectClientIsLoading);
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasTriggeredPrint, setHasTriggeredPrint] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);

  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: `${client.clientFirstName} ${client.clientLastName} LTC Report`,
    onAfterPrint: () => {
      setShowCompletionScreen(true);
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
        }
      }
    `,
  });

  const isDataReady = !clientIsLoading && client && sessionAdvisor;

  useEffect(() => {
    if (isDataReady && !hasTriggeredPrint) {
      const timer = setTimeout(() => {
        reactToPrintFn();
        setHasTriggeredPrint(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isDataReady, hasTriggeredPrint, reactToPrintFn]);

  const handleSaveAgain = () => {
    setShowCompletionScreen(false);
    setTimeout(() => {
      reactToPrintFn();
    }, 100);
  };

  const handleCloseWindow = () => {
    window.close();
  };

  if (showCompletionScreen) {
    return (
      <AuthContainer
        title={`LTC Report for ${client.clientFirstName} ${client.clientLastName}`}
      >
        <div className="space-y-6 pt-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <div className="space-y-2">
            <p className="text-base text-gray-800">
              Save the report again or close if already saved.
            </p>
          </div>

          <div className="space-y-3">
            <AuthPrimaryButton
              title="Close Window"
              onClick={handleCloseWindow}
              className="py-6 text-base"
            />
            <Button
              variant="outline"
              onClick={handleSaveAgain}
              className="w-full rounded-full py-6 text-base font-normal"
            >
              Save Report Again
            </Button>
          </div>
        </div>
      </AuthContainer>
    );
  }

  return (
    <div className="hidden">
      <OnboardingPdfReport ref={contentRef} />
    </div>
  );
}
